// MARK://dhk8/admin-shell/lib/clients/inventory
// Inventario → dhk8-erp-inventory.
//   GET    /v1/erp/warehouses?tenantId=...                          → WarehouseDto[]
//   POST   /v1/erp/warehouses              body { tenantId, name, code? }
//   GET    /v1/erp/stock-items?tenantId=...&limit=100              → { items: StockItemDto[], total }
//   POST   /v1/erp/stock-items             body { tenantId, productId, warehouseId, sku?, uom?, reorderPoint?, minQty? }
//   PATCH  /v1/erp/stock-items/:id         body { uom, reorderPoint }
//   DELETE /v1/erp/stock-items/:id         (soft delete)
//   POST   /v1/erp/stock-movements/adjust  body { tenantId, stockItemId, qty, reason }
//
// CONTRACT NOTE: a StockItem is identified by (productId, warehouseId) on the backend.
// The admin's flat InventoryItem ({ name, unit, onHand, reorderLevel, status }) is a
// thinner view of that — we expose `sku ?? productId` as the display name, `uom` as
// unit, `qty` as onHand, `reorderPoint` as reorderLevel, and derive `status`. Bug #91:
// the previous form did not collect productId/warehouseId so POSTs failed silently with
// VALIDATION_ERROR. The page now surfaces product + warehouse pickers and uses
// `createItemWithRefs` below.
import { requireTenant, apiData, svcUrl } from '../api.js'
import { PREVIEW_STUB, mock } from '../env.js'
import { authTenantId } from '../../stores/auth-store.js'
import * as M from '../mock-data.js'
import { gqlClient } from '../gql.js'
import { AdminStockItemsQuery, AdminWarehousesQuery, AdminStockMovementsQuery, AdminStockLotsQuery, AdminCreateWarehouseMutation, AdminUpdateWarehouseMutation, AdminDeleteWarehouseMutation, AdminCreateStockItemMutation, AdminUpdateStockItemMutation, AdminDeleteStockItemMutation, AdminAdjustStockMutation, AdminPackPresentationsQuery, AdminUpsertPackPresentationMutation, AdminDeletePackPresentationMutation } from '../queries/inventory.queries.js'


// ── API DTOs (dhk8-erp-inventory) ──────────────────────────────────────────
export interface ApiStockItem {
  id: string; tenantId: string; productId: string; warehouseId: string
  sku: string | null; qty: number; reservedQty: number; uom: string
  reorderPoint: number; minQty: number; active: boolean; archived: boolean
  tags: string[]; createdAt: string; updatedAt: string
}
export interface ApiWarehouse {
  id: string; tenantId: string; name: string; code: string | null
  active: boolean; createdAt: string; updatedAt: string
}

// ── UI types (warehouse picker + create payload) ───────────────────────────
export interface Warehouse { id: string; name: string; code: string | null; active: boolean }

// B / CANON §1.12 (D4): alta con XOR productId|ingredientName (exactamente uno).
// unitCostCents = costo unitario en centavos (alimenta el rollup de margen).
export interface CreateStockItemInput {
  productId?: string
  ingredientName?: string
  warehouseId: string
  unit: string
  reorderLevel: number
  sku?: string
  unitCostCents?: number
}

function deriveStatus(onHand: number, reorderLevel: number): M.InventoryItem['status'] {
  return onHand <= 0 ? 'OUT' : onHand < reorderLevel ? 'LOW' : 'OK'
}

// F8.x: el nodo del grafo de stock item trae un subconjunto de campos (id, sku,
// qty, reorderPoint, uom). Lo normalizamos a M.InventoryItem para la UI.
interface GqlStockItem { id: string; productId?: string | null; ingredientName?: string | null; sku?: string | null; qty: number; reservedQty?: number; uom: string; reorderPoint: number; unitCostCents?: number | null }
function gqlToUiItem(s: GqlStockItem): M.InventoryItem {
  const onHand = s.qty, reorderLevel = s.reorderPoint
  return { id: s.id, name: s.ingredientName ?? s.sku ?? s.productId ?? '', unit: s.uom, onHand, reorderLevel, status: deriveStatus(onHand, reorderLevel) }
}

// Vista enriquecida del stock item para los forms (incluye costo + nombre libre).
// warehouseId se expone para que la lista de Existencias muestre la columna Almacén
// (resuelta a nombre contra listWarehouses en la página).
export interface StockItemDetail extends M.InventoryItem { productId: string | null; ingredientName: string | null; unitCostCents: number | null; warehouseId: string | null; reservedQty: number }

// B / decisión Gustavo 2026-06-22: presentación de compra de un insumo.
export interface PackPresentation { id: string; productId: string; name: string; purchaseUnit: string; factor: number; baseUnit: string; active: boolean }

// ── mutable preview store ──────────────────────────────────────────────────
const db = {
  items: structuredClone(M.INVENTORY),
  warehouses: [
    { id: 'wh-principal', name: 'Almacén principal', code: 'MAIN', active: true },
  ] as Warehouse[],
}
const id = () => `i-${Math.random().toString(36).slice(2, 8)}`

function reclassify(it: M.InventoryItem): M.InventoryItem {
  it.status = deriveStatus(it.onHand, it.reorderLevel)
  return it
}

// ── warehouses ─────────────────────────────────────────────────────────────
export async function listWarehouses(signal?: AbortSignal): Promise<Warehouse[]> {
  if (PREVIEW_STUB) return mock(db.warehouses)
  if (requireTenant() === null) return []
  // F2.1 / ADR-0009: lectura por el grafo (tenant del JWT, RLS). Escrituras siguen REST.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminWarehousesQuery, {}, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.warehouses ?? []).map((w) => ({ id: w.id, name: w.name, code: w.code ?? null, active: w.active }))
}

export async function createWarehouse(input: { name: string; code?: string }): Promise<Warehouse> {
  if (PREVIEW_STUB) {
    const w: Warehouse = { id: `wh-${Math.random().toString(36).slice(2, 6)}`, name: input.name, code: input.code ?? null, active: true }
    db.warehouses.push(w)
    return mock(w)
  }
  // F8.x / ADR-0009: alta por el grafo. tenantId lo pone el JWT.
  const gqlInput: { name: string; code?: string } = { name: input.name }
  if (input.code) gqlInput.code = input.code
  const res = await gqlClient.mutation(AdminCreateWarehouseMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const w = res.data?.createWarehouse
  if (!w) throw new Error('No se pudo crear el almacén')
  return { id: w.id, name: w.name, code: w.code ?? null, active: w.active }
}

export async function updateWarehouse(id: string, input: { name?: string; code?: string | null; active?: boolean }): Promise<Warehouse> {
  if (PREVIEW_STUB) {
    const w = db.warehouses.find((x) => x.id === id)
    if (!w) throw new Error('Almacén no encontrado')
    if (input.name !== undefined) w.name = input.name
    if (input.code !== undefined) w.code = input.code
    if (input.active !== undefined) w.active = input.active
    return mock(w)
  }
  // F8.x / ADR-0009: edición por el grafo (tenant del JWT, IDOR-guard por RLS).
  const gqlInput: { name?: string; code?: string | null; active?: boolean } = {}
  if (input.name !== undefined) gqlInput.name = input.name
  if (input.code !== undefined) gqlInput.code = input.code
  if (input.active !== undefined) gqlInput.active = input.active
  const res = await gqlClient.mutation(AdminUpdateWarehouseMutation, { id, input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const w = res.data?.updateWarehouse
  if (!w) throw new Error('No se pudo actualizar el almacén')
  return { id: w.id, name: w.name, code: w.code ?? null, active: w.active }
}

export async function deleteWarehouse(id: string): Promise<void> {
  if (PREVIEW_STUB) { db.warehouses = db.warehouses.filter((x) => x.id !== id); return mock(undefined) }
  // F8.x / ADR-0009: soft-delete por el grafo (tenant del JWT, IDOR-guard por RLS).
  const res = await gqlClient.mutation(AdminDeleteWarehouseMutation, { id }).toPromise()
  if (res.error) throw res.error
}

// Transferencia entre almacenes — REST (no está en el grafo; ADR-0009 permite
// escrituras por REST). El backend valida stock suficiente y registra un
// movimiento TRANSFER src→dst. tenantId del JWT.
export async function transferStock(input: { stockItemId: string; qty: number; srcWarehouseId: string; dstWarehouseId: string }): Promise<void> {
  if (PREVIEW_STUB) return mock(undefined)
  await apiData<{ movementId: string }>(svcUrl('dhk8-erp-inventory', '/v1/erp/stock-movements/transfer'), {
    method: 'POST',
    body: { tenantId: authTenantId(), ...input },
  })
}

// ── stock items ────────────────────────────────────────────────────────────
export async function listInventory(signal?: AbortSignal, warehouseId?: string): Promise<StockItemDetail[]> {
  if (PREVIEW_STUB) return mock(db.items.map((it) => ({ ...it, productId: null, ingredientName: null, unitCostCents: null, warehouseId: db.warehouses[0]?.id ?? null, reservedQty: 0 })))
  if (requireTenant() === null) return []
  // F2.1 / ADR-0009: lectura por el grafo (tenant del JWT, RLS). El nombre del
  // producto lo resuelve la federación (StockItem.product → erp-products) para no
  // mostrar UUIDs cuando el sku es null. Escrituras siguen REST.
  // warehouseId opcional: filtra los insumos de UN almacén (drill-in de /inventory/warehouses/:id).
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminStockItemsQuery, { limit: 100 }, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.stockItems.items ?? [])
    .filter((s) => !warehouseId || s.warehouseId === warehouseId)
    .map((s) => {
      const onHand = s.qty, reorderLevel = s.reorderPoint
      // B / CANON §1.12 (D4): el nombre puede venir del Product federado, del
      // ingredientName libre (materia prima sin Product), del sku o, en último
      // caso, del productId.
      return {
        id: s.id,
        name: s.product?.name ?? s.ingredientName ?? s.sku ?? s.productId ?? '',
        unit: s.uom, onHand, reorderLevel, status: deriveStatus(onHand, reorderLevel),
        productId: s.productId ?? null, ingredientName: s.ingredientName ?? null,
        unitCostCents: s.unitCostCents ?? null, warehouseId: s.warehouseId ?? null,
        reservedQty: s.reservedQty ?? 0,
      }
    })
}

/**
 * Create a stock item bound to an existing product + warehouse. The backend requires
 * both UUIDs (CreateStockItemSchema); see services/dhk8-erp-inventory/src/schema.ts.
 * The UI form must collect them via dropdowns before calling this — the legacy
 * `createItem(input)` shape (Omit<InventoryItem, 'id' | 'status'>) cannot satisfy
 * the contract and is no longer exported.
 */
export async function createItemWithRefs(input: CreateStockItemInput): Promise<M.InventoryItem> {
  if (PREVIEW_STUB) {
    const it = reclassify({
      id: id(),
      name: input.ingredientName ?? input.sku ?? input.productId ?? '',
      unit: input.unit,
      onHand: 0,
      reorderLevel: input.reorderLevel,
      status: 'OK',
    })
    db.items.push(it)
    return mock(it)
  }
  // F8.x / ADR-0009: alta por el grafo. tenantId lo pone el JWT.
  // B / CANON §1.12 (D4): XOR — exactamente uno de productId | ingredientName.
  const hasProduct = !!input.productId
  const hasName = !!input.ingredientName && input.ingredientName.trim().length > 0
  if (hasProduct === hasName) throw new Error('Indica exactamente uno: un producto del catálogo o un nombre de insumo libre.')
  const gqlInput: { productId?: string; ingredientName?: string; warehouseId: string; uom: string; reorderPoint: number; sku?: string; unitCostCents?: number } = {
    warehouseId: input.warehouseId,
    uom: input.unit,
    reorderPoint: input.reorderLevel,
  }
  if (hasProduct) gqlInput.productId = input.productId
  if (hasName) gqlInput.ingredientName = input.ingredientName!.trim()
  if (input.sku) gqlInput.sku = input.sku
  if (input.unitCostCents != null) gqlInput.unitCostCents = Math.max(0, Math.round(input.unitCostCents))
  const res = await gqlClient.mutation(AdminCreateStockItemMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const created = res.data?.createStockItem
  if (!created) throw new Error('No se pudo crear el item de inventario')
  return gqlToUiItem(created)
}

export async function adjustStock(itemId: string, delta: number, reason?: string | null): Promise<M.InventoryItem> {
  if (PREVIEW_STUB) {
    const it = db.items.find((x) => x.id === itemId)
    if (!it) throw new Error('no encontrado')
    it.onHand = Math.max(0, Math.round((it.onHand + delta) * 100) / 100); reclassify(it)
    return mock(it, 80)
  }
  // F8.x / ADR-0009: ajuste por el grafo (tenant del JWT, IDOR-guard). El
  // resolver re-lee el stock item y lo devuelve ya con el qty nuevo. `reason` es
  // libre (ej. "Merma", "Conteo físico") y queda en el movimiento de ajuste.
  const res = await gqlClient.mutation(AdminAdjustStockMutation, { stockItemId: itemId, qty: delta, reason: reason ?? null }).toPromise()
  if (res.error) throw res.error
  const item = res.data?.adjustStock
  if (!item) throw new Error('No se pudo ajustar el inventario')
  return gqlToUiItem(item)
}

export async function updateItem(it: M.InventoryItem, unitCostCents?: number | null): Promise<M.InventoryItem> {
  if (PREVIEW_STUB) { const i = db.items.findIndex((x) => x.id === it.id); if (i >= 0) db.items[i] = reclassify(it); return mock(it) }
  // Only uom (unit) + reorderPoint (reorderLevel) + unitCostCents (A6) are editable
  // on a stock item; `name` is the linked product's name and `qty` moves via
  // stock-movements.
  // F8.x / ADR-0009: edición por el grafo (tenant del JWT, IDOR-guard). El
  // resolver re-lee y devuelve el item fresco.
  const input: { uom: string; reorderPoint: number; unitCostCents?: number } = { uom: it.unit, reorderPoint: it.reorderLevel }
  if (unitCostCents != null) input.unitCostCents = Math.max(0, Math.round(unitCostCents))
  const res = await gqlClient.mutation(AdminUpdateStockItemMutation, { id: it.id, input }).toPromise()
  if (res.error) throw res.error
  const fresh = res.data?.updateStockItem
  if (!fresh) throw new Error('No se pudo actualizar el item de inventario')
  return gqlToUiItem(fresh)
}

// ── B: presentaciones de compra (pack presentations) ───────────────────────
// Un insumo puede comprarse en presentaciones (Garrafón = 20000 MLT). factor =
// cuántas unidades base trae 1 presentación; baseUnit = la uom del stock item.
export async function listPackPresentations(productId: string, signal?: AbortSignal): Promise<PackPresentation[]> {
  if (PREVIEW_STUB) return mock([] as PackPresentation[])
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminPackPresentationsQuery, { productId }, ctx).toPromise()
  if (res.error) throw res.error
  return (res.data?.packPresentations ?? []).map((p) => ({ id: p.id, productId: p.productId, name: p.name, purchaseUnit: p.purchaseUnit, factor: p.factor, baseUnit: p.baseUnit, active: p.active }))
}
export async function upsertPackPresentation(input: { productId: string; name: string; purchaseUnit: string; factor: number; baseUnit: string; active?: boolean }): Promise<PackPresentation> {
  if (PREVIEW_STUB) return mock({ id: `pp-${Math.random().toString(36).slice(2, 8)}`, ...input, active: input.active ?? true })
  const gqlInput: { productId: string; name: string; purchaseUnit: string; factor: number; baseUnit: string; active?: boolean } = {
    productId: input.productId, name: input.name, purchaseUnit: input.purchaseUnit, factor: input.factor, baseUnit: input.baseUnit,
  }
  if (input.active !== undefined) gqlInput.active = input.active
  const res = await gqlClient.mutation(AdminUpsertPackPresentationMutation, { input: gqlInput }).toPromise()
  if (res.error) throw res.error
  const p = res.data?.upsertPackPresentation
  if (!p) throw new Error('No se pudo guardar la presentación de compra')
  return { id: p.id, productId: p.productId, name: p.name, purchaseUnit: p.purchaseUnit, factor: p.factor, baseUnit: p.baseUnit, active: p.active }
}
export async function deletePackPresentation(id: string): Promise<void> {
  if (PREVIEW_STUB) return mock(undefined)
  const res = await gqlClient.mutation(AdminDeletePackPresentationMutation, { id }).toPromise()
  if (res.error) throw res.error
}

export async function deleteItem(itemId: string): Promise<void> {
  if (PREVIEW_STUB) { db.items = db.items.filter((x) => x.id !== itemId); return mock(undefined) }
  // F8.x / ADR-0009: soft-delete por el grafo (tenant del JWT, IDOR-guard).
  const res = await gqlClient.mutation(AdminDeleteStockItemMutation, { id: itemId }).toPromise()
  if (res.error) throw res.error
}

// ── stock movements (historial) ────────────────────────────────────────────
//   GET /v1/erp/stock-movements?tenantId=...&stockItemId=...&limit=...
// El tipo del backend es libre (string); los valores canónicos del demo son
// RECEIPT / CONSUMPTION / TRANSFER / ADJUSTMENT / MERMA.
export interface StockMovement {
  id: string
  tenantId: string
  type: string
  stockItemId: string
  lotId: string | null
  qty: number
  srcWarehouseId: string | null
  dstWarehouseId: string | null
  ref: string | null
  refType: string | null
  reason: string | null
  reversed: boolean
  createdAt: string
}

export async function listMovements(signal?: AbortSignal): Promise<StockMovement[]> {
  if (PREVIEW_STUB) {
    const seed: StockMovement[] = [
      { id: 'mv-1', tenantId: 'tnt_demo', type: 'RECEIPT', stockItemId: 'i-cafe', lotId: 'lot-1', qty: 10, srcWarehouseId: null, dstWarehouseId: 'wh-principal', ref: 'OC-0001', refType: 'purchase_order', reason: 'Recepción de orden de compra', reversed: false, createdAt: new Date(Date.now() - 86400000).toISOString() },
      { id: 'mv-2', tenantId: 'tnt_demo', type: 'CONSUMPTION', stockItemId: 'i-cafe', lotId: 'lot-1', qty: -0.36, srcWarehouseId: 'wh-principal', dstWarehouseId: null, ref: 'ord-0042', refType: 'order', reason: 'Venta — Cappuccino x2', reversed: false, createdAt: new Date(Date.now() - 3600000).toISOString() },
      { id: 'mv-3', tenantId: 'tnt_demo', type: 'ADJUSTMENT', stockItemId: 'i-leche', lotId: null, qty: -1, srcWarehouseId: 'wh-principal', dstWarehouseId: null, ref: null, refType: null, reason: 'Ajuste manual (-)', reversed: false, createdAt: new Date().toISOString() },
    ]
    return mock(seed)
  }
  // F6.2 R3b / ADR-0009: historial por el grafo (tenant del JWT, RLS). El backend
  // deja `type` libre; reservedBy/lotId/etc. vienen como null cuando no aplican.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminStockMovementsQuery, { limit: 100 }, ctx).toPromise()
  if (res.error) throw res.error
  const items: StockMovement[] = (res.data?.stockMovements.items ?? []).map((m) => ({
    id: m.id,
    tenantId: authTenantId(),
    type: m.type,
    stockItemId: m.stockItemId,
    lotId: m.lotId ?? null,
    qty: m.qty,
    srcWarehouseId: m.srcWarehouseId ?? null,
    dstWarehouseId: m.dstWarehouseId ?? null,
    ref: m.ref ?? null,
    refType: m.refType ?? null,
    reason: m.reason ?? null,
    reversed: m.reversed,
    createdAt: m.createdAt,
  }))
  // Newest first.
  return items.slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

// ── stock lots (FEFO) ───────────────────────────────────────────────────────
//   GET /v1/erp/lots?tenantId=...&limit=...  (endpoint añadido en B+; ordenado
//   por expiry ASC → orden FEFO out-of-the-box).
export interface StockLot {
  id: string
  stockItemId: string
  lotNumber: string
  serialNumber: string | null
  expiryDate: string | null
  mfgDate: string | null
  qty: number
  consumedQty: number
  sku: string | null
  uom: string | null
  warehouseId: string | null
  createdAt: string
}

export async function listLots(signal?: AbortSignal): Promise<StockLot[]> {
  if (PREVIEW_STUB) {
    const day = 86400000
    const seed: StockLot[] = [
      { id: 'lot-1', stockItemId: 'i-cafe', lotNumber: 'CAFE-2605-A', serialNumber: null, expiryDate: new Date(Date.now() + 5 * day).toISOString(), mfgDate: null, qty: 4.2, consumedQty: 0.8, sku: 'SAVIS-INS-CAFE-GRANO', uom: 'KG', warehouseId: 'wh-principal', createdAt: new Date(Date.now() - 2 * day).toISOString() },
      { id: 'lot-2', stockItemId: 'i-leche', lotNumber: 'LECHE-2605', serialNumber: null, expiryDate: new Date(Date.now() + 20 * day).toISOString(), mfgDate: null, qty: 12, consumedQty: 0, sku: 'SAVIS-INS-LECHE', uom: 'LITRO', warehouseId: 'wh-principal', createdAt: new Date(Date.now() - day).toISOString() },
      { id: 'lot-3', stockItemId: 'i-jarabe', lotNumber: 'VAINILLA-A1', serialNumber: null, expiryDate: null, mfgDate: null, qty: 9, consumedQty: 0, sku: 'SAVIS-INS-JARABE-VAINILLA', uom: 'LITRO', warehouseId: 'wh-principal', createdAt: new Date(Date.now() - 3 * day).toISOString() },
    ]
    return mock(seed)
  }
  // F6.2 R3b / ADR-0009: lotes por el grafo (tenant del JWT, RLS). Ordenados en
  // cliente por expiry (FEFO) como antes; el backend ya viene por expiry ASC.
  const ctx = signal ? { fetchOptions: { signal } } : undefined
  const res = await gqlClient.query(AdminStockLotsQuery, { limit: 100 }, ctx).toPromise()
  if (res.error) throw res.error
  const items: StockLot[] = (res.data?.stockLots.items ?? []).map((l) => ({
    id: l.id,
    stockItemId: l.stockItemId,
    lotNumber: l.lotNumber,
    serialNumber: l.serialNumber ?? null,
    expiryDate: l.expiryDate ?? null,
    mfgDate: l.mfgDate ?? null,
    qty: l.qty,
    consumedQty: l.consumedQty,
    sku: l.sku ?? null,
    uom: l.uom ?? null,
    warehouseId: l.warehouseId ?? null,
    createdAt: l.createdAt,
  }))
  // FEFO: earliest expiry first, lots with no expiry last.
  return items.slice().sort((a, b) => {
    if (a.expiryDate && b.expiryDate) return a.expiryDate.localeCompare(b.expiryDate)
    if (a.expiryDate) return -1
    if (b.expiryDate) return 1
    return a.createdAt.localeCompare(b.createdAt)
  })
}
